<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type {
    OptimTurpeChart,
    OptimTurpeTemplateView,
} from "@modules/courtage/types/optim-turpe"
import { buildOptimTurpePlotlyGraph } from "@modules/courtage/utils/chart/buildOptimTurpePlotlyGraph"
import { buildOptimSoutiragePresentation } from "@modules/courtage/utils/buildOptimSoutiragePresentation"
import type { SoutirageKpiValue } from "@modules/courtage/utils/buildOptimSoutiragePresentation"
import type { BaseKpiPassThrough } from "@core/components/base/kpi/baseKpiPassThrough"

// ================================
// INTERNATIONALIZATION
// ================================
const { t, te } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            comment: "Commentaire",
            commentEnedisDenivele:
                "Enedis renvoie un dénivelé plus grand que la puissance de raccordement. L'étude a donc tourné en utilisant le maximum du dénivelé de puissance en puissance de raccordement.",
            economiesTotalDescription: "Économies totales",
            economiesTotalPerYearSuffix: "/ an",
            chartLabel: "Saison",
            noChart: "Aucun graphique exploitable pour cette analyse.",
            titles: {
                heading1:
                    "Optimisation de la partie fixe de la composante de soutirage",
                heading2:
                    "Optimisation de la partie variable de la composante de soutirage",
                heading3: "CMDPS",
                heading1PopupC5:
                    "La partie fixe de la composante de soutirage dépend de la puissance souscrite ainsi que de la Formule Tarifaire d'Acheminement (FTA)",
                heading1PopupC4:
                    "La partie fixe de la composante de soutirage dépend du dénivelé de puissance souscrit ainsi que de la Formule Tarifaire d'Acheminement (FTA)",
                heading2Popup:
                    "La partie variable de la composante de soutirage dépend de l'energie consommée et de la Formule Tarifaire d'Acheminement (FTA)",
            },
            subtitles: {
                subtitle1: "Dénivelé de puissance actuel",
                subtitle2: "Dénivelé de puissance optimal",
            },
            kpi: {
                sub: "HTVA",
                ecoFixNoValue: "Calcul des économies impossible",
                ecoVarNoValue: "Calcul des économies impossible",
                ecoFixPopup:
                    "Économies liées aux changements de la Puissance Souscrite ainsi que de la Formule Tarifaire d'Acheminement (FTA).",
                ecoVarPopup:
                    "Économies liées au changement de la Formule Tarifaire d'Acheminement (FTA)",
                ecoC4: "Économies possibles",
                ecoC4Popup:
                    "Économies liées à la modification du dénivelé de puissance ainsi que du changement de la Formule Tarifaire d'Acheminement (FTA).",
                cmdpsEvolutionPopup:
                    "Évolution du TURPE due au changement de la durée de dépassement du dénivelé de puissance.",
                ftaPartialExplainer:
                    "À cause du manque d'historique de données de consommation au pas infra-horaire, la FTA n'a été optimisée que sur les FTA ne dépendant pas de l'horosaisonnalité.",
                infraHourlyHistoricalHint:
                    "Pour optimiser la FTA actuelle, il faut un historique d'un an de données de consommation au pas infra-horaire qui ne sont pas disponibles pour ce compteur.",
                defaultPuissanceTitle: "Puissance souscrite actuelle",
                defaultPuissanceOptimaleTitle: "Puissance souscrite optimale",
                defaultEcoTitle: "Économies possibles",
                defaultEcoVarTitle: "Économies possibles",
                // DEV-1621 : un montant défavorable ne s'affiche plus sous un
                // intitulé qui promet des économies. Le mot porte le sens, le
                // montant reste positif.
                surcoutTitle: "Surcoût",
                aucuneEconomieTitle: "Aucune économie identifiée",
                cmdpsSurcoutTitle: "Surcoût",
                cmdpsEconomieTitle: "Économie",
                ftaActuelleTitle: "FTA actuelle",
                ftaOptimaleTitleBase: "FTA optimale",
                ftaOptimaleBlocked: "Optimisation de la FTA impossible",
                avantOptimTitle: "Avant optimisation",
                apresOptimTitle: "Après optimisation",
                evolutionCmdpsTitle: "Évolution",
                noDeniveleDataFta:
                    "Données insuffisantes pour afficher le dénivelé de puissance actuel.",
                noDeniveleOptimal:
                    "Données insuffisantes pour afficher le dénivelé de puissance optimal.",
            },
            rangeSeason: {
                hcb: " été",
                hpb: " été",
                hch: " hiver",
                hph: " hiver",
                pte: "",
            },
            rangeHelp: {
                hcb: "Heures Creuses saison Basse (HCB) été",
                hpb: "Heures Pleines saison Basse (HPB) été",
                hch: "Heures Creuses saison Haute (HCH) hiver",
                hph: "Heures Pleines saison Haute (HPH) hiver",
                pte: "Pointe (PTE)",
            },
            legend: {
                puissance_max: "Puissance maximale",
                abonnement_actuel: "Abonnement actuel",
                abonnement_reco: "Abonnement recommandé",
                denivele_actuel: "Dénivelé actuel",
                denivele_optimal: "Dénivelé optimal",
                intervalle_puissance: "95% des puissances appelées",
                puissance_moyenne: "Puissance moyenne",
                conso_energie: "Consommation d'électricité",
                profil_saison_haute: "Haute",
                profil_saison_haute_pointe: "Haute - Pointe",
                profil_saison_haute_hors_pointe: "Haute - Hors pointe",
                profil_saison_haute_label: "Saison haute",
                profil_saison_haute_pointe_label: "Saison haute - Pointe",
                profil_saison_haute_hors_pointe_label:
                    "Saison haute - Hors pointe",
                profil_saison_basse: "Basse",
                profil_saison_basse_label: "Saison basse",
            },
            chartOptionLabel: {
                profil_saison_haute: "Saison haute",
                profil_saison_haute_pointe: "Saison haute - Pointe",
                profil_saison_haute_hors_pointe: "Saison haute - Hors pointe",
                profil_saison_basse: "Saison basse",
                historique_puissance:
                    "Historique des puissances quotidiennes maximales appelées",
                historique_conso: "Consommation électrique journalière",
            },
            reportCta: "Télécharger le rapport PDF",
            chartTitle: {
                historique_puissance:
                    "Historique des puissances quotidiennes maximales appelées",
                historique_conso: "Consommation électrique journalière",
                profil_saison_haute:
                    "Profil hebdomadaire de la puissance apparente en saison haute",
                profil_saison_haute_c2:
                    "Profil hebdomadaire de la puissance active en saison haute",
                profil_saison_haute_pointe:
                    "Profil hebdomadaire de la puissance apparente en saison haute - Pointe",
                profil_saison_haute_pointe_c2:
                    "Profil hebdomadaire de la puissance active en saison haute - Pointe",
                profil_saison_haute_hors_pointe:
                    "Profil hebdomadaire de la puissance apparente en saison haute - Hors pointe",
                profil_saison_haute_hors_pointe_c2:
                    "Profil hebdomadaire de la puissance active en saison haute - Hors pointe",
                profil_saison_basse:
                    "Profil hebdomadaire de la puissance apparente en saison basse",
                profil_saison_basse_c2:
                    "Profil hebdomadaire de la puissance active en saison basse",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    analyse: OptimTurpeTemplateView
    /** Affiche le bouton PDF (détail historique, aligné menu « Télécharger » legacy). */
    showReportDownload?: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

const emit = defineEmits<{
    "click:report": []
}>()

// ================================
// STATE
// ================================
/**
 * Pass-through PrimeVue des cartes KPI résultat (grille dense, bordure légère).
 */
const optimResultKpiCardPt: BaseKpiPassThrough = {
    root: "!max-w-none w-full min-h-0 h-auto min-w-0 rounded-md border border-surface-200 p-6 gap-4 bg-white shadow-none justify-start items-stretch mx-0 sm:max-w-none",
    title: "normal-case text-xs font-medium text-primary-600 flex flex-wrap items-center justify-center gap-1",
    valueRow:
        "flex flex-col w-full min-w-0 flex-1 justify-start items-center text-center gap-1 min-h-[2.5rem]",
    value: "text-xl font-semibold text-primary-900",
}

/**
 * Index du graphique Plotly sélectionné dans le sélecteur.
 */
const currentChartIndex = ref(0)

/**
 * Commentaire brut renvoyé par Enedis quand le dénivelé dépasse la puissance de raccordement.
 */
const ENEDIS_DENIVELE_COMMENT =
    "Enedis renvoie un dénivelé plus grand que la puissance de raccordement."

// ================================
// COMPUTED
// ================================
/**
 * Texte d’alerte commentaire (équivalent `component.optim_analyse_content.comment`).
 */
const getCommentDisplay = computed(() => {
    const raw = props.analyse.comment?.trim() ?? ""
    if (raw === ENEDIS_DENIVELE_COMMENT) {
        return String(t("commentEnedisDenivele"))
    }
    return raw
})

/**
 * Segment affiché pour les intitulés de graphiques (C2 / P1 = profils « puissance active »).
 */
const getAnalyseSegment = computed(
    () => props.analyse.segment?.trim().toUpperCase() ?? ""
)

/**
 * Montant total d’économies (carte + bandeau, aligné `economies_total` homeys-front).
 */
const getEconomiesTotalDisplay = computed(
    () => props.analyse.economiesTotal?.trim() || "—"
)

/** Graphique densité par classe temporelle : non affiché dans ce résultat. */
const CHART_NAME_DENSITE_CLASSE = "densite_classe_temporelle"

const getDisplayableCharts = computed(() =>
    props.analyse.charts.filter((c) => c.name !== CHART_NAME_DENSITE_CLASSE)
)

/**
 * Libellé de légende Plotly à partir du nom d’API (i18n ou repli sur le nom brut).
 * @param apiName - Identifiant série côté API.
 * @returns Texte affiché dans la légende.
 */
function resolveOptimTurpeLegendLabel(apiName: string | undefined): string {
    if (!apiName) return ""
    const key = `legend.${apiName}`
    return te(key) ? String(t(key)) : apiName
}

/**
 * Titre affiché du graphique (variantes C2/P1 pour les profils saison).
 * @param chart - Métadonnées graphique API.
 * @param segmentUpper - Segment normalisé (C2, P1, …).
 * @returns Titre pour en-tête ou liste.
 */
function resolveOptimTurpeChartTitle(
    chart: OptimTurpeChart | undefined,
    segmentUpper: string
): string {
    if (!chart) return ""
    const name = chart.name ?? ""
    const isC2Like = segmentUpper === "C2" || segmentUpper === "P1"

    if (name === "profil_saison_haute") {
        return String(
            t(
                isC2Like
                    ? "chartTitle.profil_saison_haute_c2"
                    : "chartTitle.profil_saison_haute"
            )
        )
    }
    if (name === "profil_saison_haute_pointe") {
        return String(
            t(
                isC2Like
                    ? "chartTitle.profil_saison_haute_pointe_c2"
                    : "chartTitle.profil_saison_haute_pointe"
            )
        )
    }
    if (name === "profil_saison_haute_hors_pointe") {
        return String(
            t(
                isC2Like
                    ? "chartTitle.profil_saison_haute_hors_pointe_c2"
                    : "chartTitle.profil_saison_haute_hors_pointe"
            )
        )
    }
    if (name === "profil_saison_basse") {
        return String(
            t(
                isC2Like
                    ? "chartTitle.profil_saison_basse_c2"
                    : "chartTitle.profil_saison_basse"
            )
        )
    }

    const nameKey = name ? `chartTitle.${name}` : ""
    if (nameKey && te(nameKey)) return String(t(nameKey))
    return chart.title ?? chart.name ?? ""
}

/**
 * Libellé court du sélecteur de graphique (option i18n ou titre long).
 * @param chart - Métadonnées graphique API.
 * @param index - Position dans la liste (repli « Graphique n »).
 * @returns Texte pour BaseSelect.
 */
function resolveOptimTurpeChartDropdownLabel(
    chart: OptimTurpeChart | undefined,
    index: number
): string {
    if (!chart) return `Graphique ${index + 1}`
    const name = chart.name ?? ""
    const optKey = name ? `chartOptionLabel.${name}` : ""
    if (optKey && te(optKey)) return String(t(optKey))
    return (
        resolveOptimTurpeChartTitle(chart, getAnalyseSegment.value) ||
        `Graphique ${index + 1}`
    )
}

const getChartOptions = computed(() =>
    getDisplayableCharts.value.map((chart, index) => ({
        name: resolveOptimTurpeChartTitle(chart, getAnalyseSegment.value),
        dropdownLabel: resolveOptimTurpeChartDropdownLabel(chart, index),
        value: index,
    }))
)

const getCurrentChart = computed(
    () => getDisplayableCharts.value[currentChartIndex.value] ?? undefined
)

const getCurrentGraph = computed(() =>
    buildOptimTurpePlotlyGraph(getCurrentChart.value, {
        legendLabel: resolveOptimTurpeLegendLabel,
    })
)

const getCurrentChartDisplayTitle = computed(() =>
    resolveOptimTurpeChartTitle(getCurrentChart.value, getAnalyseSegment.value)
)

/** Présentation des blocs partie fixe / variable / CMDPS (libellés et KPI). */
const getSoutiragePresentation = computed(() =>
    buildOptimSoutiragePresentation(
        props.analyse.analysisData,
        props.analyse.segment ?? "",
        {
            subtitleActual: String(t("subtitles.subtitle1")),
            subtitleOptimal: String(t("subtitles.subtitle2")),
            ecoC4SingleLabel: String(t("kpi.ecoC4")),
            rangeSeason: {
                hcb: String(t("rangeSeason.hcb")),
                hpb: String(t("rangeSeason.hpb")),
                hch: String(t("rangeSeason.hch")),
                hph: String(t("rangeSeason.hph")),
                pte: String(t("rangeSeason.pte")),
            },
            rangeHelp: {
                hcb: String(t("rangeHelp.hcb")),
                hpb: String(t("rangeHelp.hpb")),
                hch: String(t("rangeHelp.hch")),
                hph: String(t("rangeHelp.hph")),
                pte: String(t("rangeHelp.pte")),
            },
            defaultPuissanceTitle: String(t("kpi.defaultPuissanceTitle")),
            defaultPuissanceOptimaleTitle: String(
                t("kpi.defaultPuissanceOptimaleTitle")
            ),
            defaultEcoTitle: String(t("kpi.defaultEcoTitle")),
            defaultEcoVarTitle: String(t("kpi.defaultEcoVarTitle")),
            ftaActuelleTitle: String(t("kpi.ftaActuelleTitle")),
            ftaOptimaleTitleBase: String(t("kpi.ftaOptimaleTitleBase")),
            ftaOptimaleBlocked: String(t("kpi.ftaOptimaleBlocked")),
            ftaPartialExplainer: String(t("kpi.ftaPartialExplainer")),
            avantOptimTitle: String(t("kpi.avantOptimTitle")),
            apresOptimTitle: String(t("kpi.apresOptimTitle")),
            evolutionCmdpsTitle: String(t("kpi.evolutionCmdpsTitle")),
            surcoutTitle: String(t("kpi.surcoutTitle")),
            aucuneEconomieTitle: String(t("kpi.aucuneEconomieTitle")),
            cmdpsSurcoutTitle: String(t("kpi.cmdpsSurcoutTitle")),
            cmdpsEconomieTitle: String(t("kpi.cmdpsEconomieTitle")),
            noDeniveleDataFta: String(t("kpi.noDeniveleDataFta")),
            noDeniveleOptimal: String(t("kpi.noDeniveleOptimal")),
            ecoFixPopup: String(t("kpi.ecoFixPopup")),
            ecoVarPopup: String(t("kpi.ecoVarPopup")),
            ecoC4Popup: String(t("kpi.ecoC4Popup")),
            cmdpsEvolutionPopup: String(t("kpi.cmdpsEvolutionPopup")),
            infraHourlyHistoricalHint: String(
                t("kpi.infraHourlyHistoricalHint")
            ),
        }
    )
)

const getFixTitlePopup = computed(() =>
    getSoutiragePresentation.value.fixBlock.popupIsC5
        ? String(t("titles.heading1PopupC5"))
        : String(t("titles.heading1PopupC4"))
)

// ================================
// WATCHERS
// ================================
/**
 * Réinitialise le graphique sélectionné lorsque l’on affiche une autre analyse.
 */
watch(
    () => props.analyse._id,
    () => {
        currentChartIndex.value = 0
    }
)

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Classes Tailwind pour la valeur d’une ligne KPI (succès / danger / neutre).
 * @param row - Ligne présentation soutirage.
 * @returns Classes CSS pour le paragraphe valeur.
 */
function buildKpiValueClasses(row: SoutirageKpiValue): string {
    if (row.variant === "success") {
        return "text-emerald-700 font-semibold"
    }
    if (row.variant === "danger") {
        return "text-red-600 font-semibold"
    }
    return "text-primary-700 font-semibold"
}

/**
 * Message lorsque les économies ne sont pas calculables (partie fixe ou variable).
 * @param row - Ligne KPI avec `emptyReason`.
 * @returns Libellé vide ou texte i18n.
 */
function resolveEmptyEconomyLabel(row: SoutirageKpiValue): string {
    if (row.emptyReason === "ecoFix") {
        return String(t("kpi.ecoFixNoValue"))
    }
    if (row.emptyReason === "ecoVar") {
        return String(t("kpi.ecoVarNoValue"))
    }
    return ""
}

// ================================
// INTERACTIONS
// ================================
/** Relais vers la page / parent pour la même logique que l’icône PDF du tableau. */
function onClickReport(): void {
    emit("click:report")
}
</script>

<template>
    <!-- ================================ -->
    <!-- OPTIM TURPE — résultat (soutirage + graphiques) -->
    <!-- ================================ -->
    <div class="optim-turpe-result-content flex flex-col gap-8">
        <div v-if="showReportDownload" class="flex flex-wrap justify-end gap-2">
            <BaseButton
                :label="t('reportCta')"
                icon="pi pi-file-pdf"
                size="small"
                outlined
                severity="secondary"
                @click="onClickReport"
            />
        </div>
        <BaseMessage v-if="analyse.comment" severity="warn">
            {{ t("comment") }}: {{ getCommentDisplay }}
        </BaseMessage>

        <!-- Synthèse économies totales (équivalent `optim-analyse-content__total_economies`) -->
        <div
            class="flex flex-wrap items-center justify-center gap-2 rounded-md bg-primary-50 px-4 py-12 text-center max-sm:flex-col max-sm:gap-6"
        >
            <p class="text-base font-medium text-primary-800">
                {{ t("economiesTotalDescription") }}
            </p>
            <p
                class="flex flex-wrap items-baseline justify-center gap-1 text-2xl font-medium text-emerald-800"
            >
                <span>{{ getEconomiesTotalDisplay }}</span>
                <sub class="text-xs font-normal text-primary-600">{{
                    t("kpi.sub")
                }}</sub>
                <span class="text-base font-normal text-primary-900">{{
                    t("economiesTotalPerYearSuffix")
                }}</span>
            </p>
        </div>

        <!-- Partie fixe -->
        <section class="flex flex-col gap-2">
            <div
                class="flex flex-wrap items-center justify-center gap-3 rounded-md bg-surface-100 px-4 py-3 text-center text-base font-medium text-primary-700"
            >
                <span>{{ t("titles.heading1") }}</span>
                <i
                    v-tooltip.top="getFixTitlePopup"
                    class="pi pi-info-circle cursor-help text-primary-600"
                    :aria-label="getFixTitlePopup"
                />
            </div>

            <div
                v-if="getSoutiragePresentation.showDeniveleBlocks"
                class="flex flex-col gap-6"
            >
                <div
                    class="flex flex-col gap-4 rounded-md border border-surface-200 p-6"
                >
                    <p class="text-center text-sm font-medium text-primary-700">
                        {{ getSoutiragePresentation.deniveleActual.subtitle }}
                    </p>
                    <div
                        v-if="getSoutiragePresentation.deniveleActual.isEmpty"
                        class="flex flex-wrap items-center justify-center gap-2 text-center text-sm text-primary-600"
                    >
                        {{
                            getSoutiragePresentation.deniveleActual.noDataBanner
                        }}
                        <i
                            v-tooltip.top="
                                getSoutiragePresentation.deniveleActual
                                    .noDataBanner
                            "
                            class="pi pi-info-circle cursor-help text-primary-400"
                            aria-hidden="true"
                        />
                    </div>
                    <div
                        v-else
                        class="flex flex-wrap justify-around gap-4 max-md:flex-col max-md:items-center"
                    >
                        <div
                            v-for="pl in getSoutiragePresentation.deniveleActual
                                .plages"
                            :key="`da-${pl.plageKey}`"
                            class="flex min-w-[12rem] flex-col gap-2 text-center"
                        >
                            <p class="text-xs font-medium text-primary-600">
                                {{ pl.label }}
                                <i
                                    v-tooltip.top="pl.labelHelp"
                                    class="pi pi-info-circle ml-1 cursor-help align-middle text-primary-400"
                                    :aria-label="pl.labelHelp"
                                />
                            </p>
                            <p class="text-lg font-semibold text-primary-900">
                                {{ pl.displayValue }}
                            </p>
                        </div>
                    </div>
                </div>

                <div
                    class="flex flex-col gap-4 rounded-md border border-surface-200 p-6"
                >
                    <p class="text-center text-sm font-medium text-primary-700">
                        {{ getSoutiragePresentation.deniveleOptimal.subtitle }}
                    </p>
                    <div
                        v-if="getSoutiragePresentation.deniveleOptimal.isEmpty"
                        class="flex flex-wrap items-center justify-center gap-2 text-center text-sm text-primary-600"
                    >
                        {{
                            getSoutiragePresentation.deniveleOptimal
                                .noDataBanner
                        }}
                        <i
                            v-tooltip.top="
                                getSoutiragePresentation.deniveleOptimal
                                    .noDataBanner
                            "
                            class="pi pi-info-circle cursor-help text-primary-400"
                            aria-hidden="true"
                        />
                    </div>
                    <div
                        v-else
                        class="flex flex-wrap justify-around gap-4 max-md:flex-col max-md:items-center"
                    >
                        <div
                            v-for="pl in getSoutiragePresentation
                                .deniveleOptimal.plages"
                            :key="`do-${pl.plageKey}`"
                            class="flex min-w-[12rem] flex-col gap-2 text-center"
                        >
                            <p class="text-xs font-medium text-primary-600">
                                {{ pl.label }}
                                <i
                                    v-tooltip.top="pl.labelHelp"
                                    class="pi pi-info-circle ml-1 cursor-help align-middle text-primary-400"
                                    :aria-label="pl.labelHelp"
                                />
                            </p>
                            <p class="text-lg font-semibold text-primary-900">
                                {{ pl.displayValue }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(14rem,1fr))]"
            >
                <BaseKPI
                    v-for="(row, idx) in getSoutiragePresentation.fixBlock.rows"
                    :key="`fix-${idx}`"
                    :label="row.label"
                    :value="
                        row.displayValue != null && row.displayValue !== ''
                            ? String(row.displayValue)
                            : '—'
                    "
                    :type="`optim-turpe-fix-${idx}`"
                    :clickable="false"
                    :pt="optimResultKpiCardPt"
                >
                    <template #title>
                        <span>{{ row.label }}</span>
                        <i
                            v-if="row.labelPopup"
                            v-tooltip.top="row.labelPopup"
                            class="pi pi-info-circle cursor-help text-primary-400"
                            aria-hidden="true"
                        />
                    </template>
                    <template #value>
                        <template v-if="row.displayValue || row.noValueText">
                            <p :class="['text-xl', buildKpiValueClasses(row)]">
                                {{ row.displayValue }}
                                <span
                                    v-if="row.sub && row.displayValue"
                                    class="align-baseline text-xs font-normal text-primary-600"
                                >
                                    <sub>{{ row.sub }}</sub>
                                </span>
                                <span
                                    v-if="row.unit && row.displayValue"
                                    class="text-sm font-normal text-primary-600"
                                >
                                    / {{ row.unit }}
                                </span>
                            </p>
                            <p
                                v-if="
                                    row.noValueText &&
                                    !row.displayValue &&
                                    !row.emptyReason
                                "
                                class="mt-1 flex flex-wrap items-center justify-center gap-1 text-sm text-primary-600"
                            >
                                {{ row.noValueText }}
                                <i
                                    v-if="row.labelSecondaryPopup"
                                    v-tooltip.top="row.labelSecondaryPopup"
                                    class="pi pi-info-circle cursor-help text-primary-400"
                                    aria-hidden="true"
                                />
                            </p>
                            <p
                                v-if="
                                    row.noValueText &&
                                    row.displayValue &&
                                    row.labelSecondaryPopup
                                "
                                class="mt-1 text-xs text-primary-600"
                            >
                                {{ row.noValueText }}
                            </p>
                            <p
                                v-if="row.emptyReason && !row.displayValue"
                                class="mt-1 flex flex-wrap items-center justify-center gap-1 text-sm text-primary-600"
                            >
                                {{ resolveEmptyEconomyLabel(row) }}
                                <i
                                    v-if="row.valuePopup"
                                    v-tooltip.top="row.valuePopup"
                                    class="pi pi-info-circle cursor-help text-primary-400"
                                    aria-hidden="true"
                                />
                            </p>
                            <p
                                v-if="
                                    row.displayValue &&
                                    row.valuePopup &&
                                    !row.emptyReason
                                "
                                class="mt-1 flex justify-center"
                            >
                                <i
                                    v-tooltip.top="row.valuePopup"
                                    class="pi pi-info-circle cursor-help text-primary-400"
                                    aria-hidden="true"
                                />
                            </p>
                        </template>
                        <template v-else-if="row.emptyReason">
                            <p
                                class="flex flex-wrap items-center justify-center gap-1 text-sm text-primary-600"
                            >
                                {{ resolveEmptyEconomyLabel(row) }}
                                <i
                                    v-if="row.labelPopup"
                                    v-tooltip.top="row.labelPopup"
                                    class="pi pi-info-circle cursor-help text-primary-400"
                                    aria-hidden="true"
                                />
                            </p>
                        </template>
                    </template>
                </BaseKPI>
            </div>
        </section>

        <!-- Partie variable -->
        <section class="flex flex-col gap-2">
            <div
                class="flex flex-wrap items-center justify-center gap-3 rounded-md bg-surface-100 px-4 py-3 text-center text-base font-medium text-primary-700"
            >
                <span>{{ t("titles.heading2") }}</span>
                <i
                    v-tooltip.top="String(t('titles.heading2Popup'))"
                    class="pi pi-info-circle cursor-help text-primary-600"
                    :aria-label="String(t('titles.heading2Popup'))"
                />
            </div>

            <div
                class="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(14rem,1fr))]"
            >
                <BaseKPI
                    v-for="(row, idx) in getSoutiragePresentation.varBlock.rows"
                    :key="`var-${idx}`"
                    :label="row.label"
                    :value="
                        row.displayValue != null && row.displayValue !== ''
                            ? String(row.displayValue)
                            : '—'
                    "
                    :type="`optim-turpe-var-${idx}`"
                    :clickable="false"
                    :pt="optimResultKpiCardPt"
                >
                    <template #title>
                        <span>{{ row.label }}</span>
                        <i
                            v-if="row.labelPopup"
                            v-tooltip.top="row.labelPopup"
                            class="pi pi-info-circle cursor-help text-primary-400"
                            aria-hidden="true"
                        />
                        <i
                            v-if="row.labelSecondaryPopup"
                            v-tooltip.top="row.labelSecondaryPopup"
                            class="pi pi-info-circle cursor-help text-primary-400"
                            aria-hidden="true"
                        />
                    </template>
                    <template #value>
                        <template v-if="row.noValueText && !row.displayValue">
                            <p
                                class="flex flex-wrap items-center justify-center gap-1 text-sm text-primary-600"
                            >
                                {{ row.noValueText }}
                                <i
                                    v-if="row.valuePopup"
                                    v-tooltip.top="row.valuePopup"
                                    class="pi pi-info-circle cursor-help text-primary-400"
                                    aria-hidden="true"
                                />
                            </p>
                        </template>
                        <template v-else-if="row.displayValue">
                            <p :class="['text-xl', buildKpiValueClasses(row)]">
                                {{ row.displayValue }}
                                <span
                                    v-if="row.sub"
                                    class="align-baseline text-xs font-normal text-primary-600"
                                >
                                    <sub>{{ row.sub }}</sub>
                                </span>
                                <span
                                    v-if="row.unit"
                                    class="text-sm font-normal text-primary-600"
                                >
                                    / {{ row.unit }}
                                </span>
                            </p>
                            <p
                                v-if="row.valuePopup && idx === 1"
                                class="mt-1 flex justify-center"
                            >
                                <i
                                    v-tooltip.top="row.valuePopup"
                                    class="pi pi-info-circle cursor-help text-primary-400"
                                    aria-hidden="true"
                                />
                            </p>
                        </template>
                        <template v-else-if="row.emptyReason">
                            <p
                                class="flex flex-wrap items-center justify-center gap-1 text-sm text-primary-600"
                            >
                                {{ resolveEmptyEconomyLabel(row) }}
                                <i
                                    v-if="row.labelPopup"
                                    v-tooltip.top="row.labelPopup"
                                    class="pi pi-info-circle cursor-help text-primary-400"
                                    aria-hidden="true"
                                />
                                <i
                                    v-if="row.valuePopup"
                                    v-tooltip.top="row.valuePopup"
                                    class="pi pi-info-circle cursor-help text-primary-400"
                                    aria-hidden="true"
                                />
                            </p>
                        </template>
                    </template>
                </BaseKPI>
            </div>
        </section>

        <!-- CMDPS -->
        <section
            v-if="getSoutiragePresentation.cmdpsBlock"
            class="flex flex-col gap-2"
        >
            <div
                class="rounded-md bg-surface-100 px-4 py-3 text-center text-base font-medium text-primary-700"
            >
                {{ t("titles.heading3") }}
            </div>
            <div
                class="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(14rem,1fr))]"
            >
                <BaseKPI
                    v-for="(row, idx) in getSoutiragePresentation.cmdpsBlock
                        .rows"
                    :key="`cmdps-${idx}`"
                    :label="row.label"
                    :value="
                        row.displayValue != null && row.displayValue !== ''
                            ? String(row.displayValue)
                            : '—'
                    "
                    :type="`optim-turpe-cmdps-${idx}`"
                    :clickable="false"
                    :pt="optimResultKpiCardPt"
                >
                    <template #title>
                        <span>{{ row.label }}</span>
                        <i
                            v-if="row.labelPopup"
                            v-tooltip.top="row.labelPopup"
                            class="pi pi-info-circle cursor-help text-primary-400"
                            aria-hidden="true"
                        />
                    </template>
                    <template #value>
                        <p :class="['text-xl', buildKpiValueClasses(row)]">
                            {{ row.displayValue }}
                            <span
                                v-if="row.sub"
                                class="align-baseline text-xs font-normal text-primary-600"
                            >
                                <sub>{{ row.sub }}</sub>
                            </span>
                            <span
                                v-if="row.unit"
                                class="text-sm font-normal text-primary-600"
                            >
                                / {{ row.unit }}
                            </span>
                        </p>
                    </template>
                </BaseKPI>
            </div>
        </section>

        <!-- Graphiques (titre + sélecteur, légende Plotly dans PlotlyChart) -->
        <section class="flex flex-col gap-6">
            <template v-if="getCurrentGraph">
                <div
                    class="flex flex-wrap items-center justify-between gap-4 max-sm:flex-col"
                >
                    <div class="min-w-0 flex-1 text-center sm:text-left">
                        <p
                            class="text-base font-medium text-primary-800 sm:text-lg"
                        >
                            {{ getCurrentChartDisplayTitle }}
                        </p>
                    </div>
                    <BaseSelect
                        v-if="getChartOptions.length > 1"
                        v-model="currentChartIndex"
                        :options="getChartOptions"
                        option-label="dropdownLabel"
                        option-value="value"
                        class="min-w-48 shrink-0 self-start sm:self-auto"
                        :placeholder="t('chartLabel')"
                    />
                </div>

                <div class="relative min-h-96 w-full overflow-hidden">
                    <PlotlyChart :graph="getCurrentGraph" />
                </div>
            </template>
            <BaseMessage v-else severity="info">
                {{ t("noChart") }}
            </BaseMessage>
        </section>
    </div>
</template>
